import { mapState } from 'vuex';
import ApiService from '@/services/apiService/lambda';
import inputRules from '@/mixins/input-rules';
import { get } from 'lodash';
import CodemirrorWrapper from './codemirror-wrapper.vue';
export default {
  name: 'CoreScriptEditor',
  components: {
    CodemirrorWrapper
  },
  mixins: [inputRules],
  model: {
    prop: 'script',
    event: 'update:script'
  },
  props: {
    script: {
      type: String,
      required: false,
      default: ''
    },
    scriptType: {
      type: String,
      required: false,
      default: 'javascript'
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false
    },
    scriptParamsTransformer: {
      type: Function,
      required: false,
      default: null
    },
    /**
     * Specify a function to evaluate the script
     * This will be used instead of the default evaluation function
     * @type {Function}
     * @default null
     * @example
     * evaluationFunction: (payload, additionalFormData) => { return { result: script(payload), logs: ['info: log message'], error: null } }
     */
    evaluationFunction: {
      type: Function,
      required: false,
      default: null
    },
    hideScriptTypeSelector: {
      type: Boolean,
      required: false,
      default: false
    },
    hidePayloadInputs: {
      type: Boolean,
      required: false,
      default: false
    },
    fixedPayloadType: {
      type: String,
      required: false,
      default: null
    }
  },
  data() {
    return {
      result: '',
      error: '',
      logs: [],
      showResult: false,
      formIsValid: false,
      payloadType: 'HEX',
      payload: '',
      isParsing: false,
      scriptInternal: this.script,
      scriptTypeInternal: this.scriptType
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled', 'UiTheme']),
    payloadValidationError() {
      if (!this.payload) {
        return undefined;
      }
      if (this.actualPayloadType === 'HEX' && this.hexPayloadIsValid) {
        return undefined;
      }
      if (this.actualPayloadType === 'JSON' && this.jsonPayloadIsValid) {
        return undefined;
      }
      return this.$i18n_t('Core.ScriptEditor.payloadTypeError');
    },
    hexPayloadIsValid() {
      if (!this.payload) {
        return false;
      }
      const payloadHasEvenLength = this.payload.length % 2 === 0;
      if (!payloadHasEvenLength) {
        return false;
      }
      const payloadHasOnlyHexChars = /^[a-fA-F0-9]+$/.exec(this.payload) !== null;
      return payloadHasOnlyHexChars;
    },
    jsonPayloadIsValid() {
      if (!this.payload) {
        return false;
      }
      try {
        JSON.parse(this.payload);
        return true;
      } catch {
        return false;
      }
    },
    cmOptions() {
      return {
        readOnly: this.readonly ? 'nocursor' : false,
        tabSize: 4,
        mode: {
          name: 'javascript',
          json: true
        },
        firstLineNumber: 0,
        theme: this.UiTheme ? 'base16-dark' : 'duotone-light',
        lineNumbers: true,
        line: true,
        foldGutter: true,
        styleActiveLine: true,
        styleSelectedText: true,
        hintOptions: {
          completeSingle: false
        },
        lineWrapping: false
      };
    },
    actualPayloadType() {
      if (this.fixedPayloadType) {
        return this.fixedPayloadType;
      }
      return this.payloadType;
    }
  },
  watch: {
    script: {
      handler() {
        this.scriptInternal = this.script;
        this.refreshCM();
      },
      immediate: true
    },
    scriptType: {
      handler() {
        this.scriptTypeInternal = this.scriptType;
        this.refreshCM();
      },
      immediate: true
    },
    scriptTypeInternal() {
      this.emitChanges();
    },
    scriptInternal() {
      this.emitChanges();

      // clear codemirror line classes
      if (this.$refs.codemirror && this.$refs.codemirror.cmInstance && this.$refs.codemirror.cmInstance.doc) {
        try {
          let lineNumber = 0;
          this.$refs.codemirror.cmInstance.doc.eachLine(() => {
            this.$refs.codemirror.cmInstance.removeLineClass(lineNumber, 'background', 'codemirrorScriptEditorSyntaxError');
            lineNumber++;
          });
        } catch (error) {
          console.warn('Error clearing CodeMirror line classes:', error);
        }
      }
    }
  },
  mounted() {
    this.refreshCM();
  },
  beforeDestroy() {
    // clean up any references to the codemirror instance
    if (this.$refs.codemirror) {
      this.$refs.codemirror = null;
    }
  },
  methods: {
    emitChanges() {
      this.$emit('update:script-type', this.scriptTypeInternal);
      this.$emit('update:script', this.scriptInternal);
    },
    logStyle(logMessage) {
      const logType = logMessage.split(':')[0];
      switch (logType) {
        case 'log':
          return '';
        case 'info':
          return 'color: deepskyblue;';
        case 'warn':
          return 'color: orange;';
        case 'error':
          return 'color: red;';
        case 'debug':
          return 'color: gray;';
        default:
          return '';
      }
    },
    copySuccess() {
      this.$snack(this.$i18n_t('Common.copyToClipboardSuccess'), 'success');
    },
    // eslint-disable-next-line max-statements
    async evaluateScript() {
      const formData = new FormData(this.$refs.testForm.$el);
      const paramsFromForm = Object.fromEntries(formData);
      let parsedPayload = this.payload;
      switch (this.actualPayloadType) {
        case 'JSON':
          parsedPayload = JSON.parse(this.payload);
          break;
        case 'HEX':
          break;
        default:
          throw new Error('Unknown payload type');
      }
      let nextResult = '';
      let nextError = '';
      let nextLogs = [];
      try {
        this.isParsing = true;
        let response;
        if (typeof this.evaluationFunction === 'function') {
          response = await this.evaluationFunction(parsedPayload, paramsFromForm);
        } else {
          let params = [parsedPayload];
          if (typeof this.scriptParamsTransformer === 'function') {
            params = this.scriptParamsTransformer(parsedPayload, paramsFromForm);
          }
          response = await ApiService.evaluateScript({
            script: this.scriptInternal,
            scriptParams: params
          });
        }
        if (!response) {
          response = {
            error: 'No response from server'
          };
        }
        nextResult = this.$i18n_t('Core.ScriptEditor.tester.noResponse');
        if (response.result !== undefined) {
          nextResult = response.result;
          nextError = '';
        }
        if (response.error !== undefined && response.error !== null) {
          nextError = response.error;
          nextResult = '';
        }
        nextLogs = response.logs;
      } catch (e) {
        nextResult = '';
        if (e.isAxiosError) {
          nextError = get(e, 'response.data', e.message);
          return;
        }
        nextError = e;
      } finally {
        this.isParsing = false;
        const LINE_OFFSET = 24;
        if (nextError && typeof nextError === 'string') {
          const hasTranslation = this.$i18n_te(`Error.${nextError}`);
          if (hasTranslation) {
            this.error = this.$i18n_t(`Error.${nextError}`);
          } else if (typeof nextError === 'string') {
            nextError = nextError.split('[Line ').map(linePart => {
              const [lineNumber, ...rest] = linePart.split(']');
              const parsedLineNumber = parseInt(lineNumber, 10);
              if (isNaN(parsedLineNumber)) {
                return linePart;
              }
              const actualLineNumber = parsedLineNumber - LINE_OFFSET;
              if (this.$refs.codemirror && this.$refs.codemirror.cmInstance) {
                try {
                  this.$refs.codemirror.cmInstance.addLineClass(actualLineNumber, 'background', 'codemirrorScriptEditorSyntaxError');
                } catch (error) {
                  console.warn('Error adding line class to CodeMirror:', error);
                }
              }
              return `${actualLineNumber}] ${rest.join(']')}`;
            }).join('[Line ');
          }
        }
        if (typeof nextError !== 'string' && nextError.stack !== undefined) {
          // fix line numbers in stack trace by using LINE_OFFSET
          // e.g.
          // original: at checkOffset (<isolated-vm>:1269:36)
          // fixed: at checkOffset (eval:1246:36)

          nextError.stack = nextError.stack.split('<isolated-vm>:').map(linePart => {
            const [lineNumber, ...rest] = linePart.split(':');
            const fixedLineNumber = parseInt(lineNumber, 10) - LINE_OFFSET;
            if (isNaN(fixedLineNumber)) {
              return linePart;
            }
            return `${fixedLineNumber}:${rest.join(':')}`;
          }).join('<eval>:');
        }
        this.result = nextResult;
        this.error = nextError;
        this.logs = nextLogs;
        this.showResult = true;
      }
    },
    validateForm() {
      this.$refs.testForm.validate();
    },
    async refreshCM() {
      // not sure why, but we need to wait 2 ticks before the code is available for rendering...
      await this.$nextTick();
      await this.$nextTick();
      if (!this.$refs.codemirror) {
        return;
      }

      // check if the codemirror instance exists before trying to access
      if (this.$refs.codemirror.cmInstance && typeof this.$refs.codemirror.cmInstance.refresh === 'function') {
        try {
          // required workaround to get the cursor appearing at the right spot
          // https://discuss.codemirror.net/t/codemirror-cursor-is-not-partially-visible/648/6
          this.$refs.codemirror.cmInstance.refresh();
        } catch (error) {
          console.warn('Error refreshing CodeMirror:', error);
        }
      }
    }
  }
};